import type { IWebApiResult } from "api/interfaces/data/IWebApiResult";
import type { IEmployeeHistoryDto } from "api/interfaces/Employees/IEmployeeHistoryDto";
import type { IUserDto } from "api/interfaces/users/IUserDto";
import type { IUserExistCheckerDto } from "api/interfaces/users/IUserExistCheckerDto";
import type { IUserFullDto } from "api/interfaces/users/IUserFullDto";
import type { IUserInitialDto } from "api/interfaces/users/IUserInitialDto";
import type { IUserRestoreDto } from "api/interfaces/users/IUserRestoreDto";
import type { IUserSaveDto } from "api/interfaces/users/IUserSaveDto";
import type { IUserShortDto } from "api/interfaces/users/IUserShortDto";
import type { IUserSiteConfigDto } from "api/interfaces/users/IUserSiteConfigDto";

import type { UserRoleEnum } from "api/enums/Users/UserRoleEnum";
import type { UserStatusEnum } from "api/enums/Users/UserStatusEnum";

import type { IUserBlackListDto } from "api/interfaces/users/IUserBlackListDto";

import type { IUserBlackListAddDto } from "api/interfaces/users/IUserBlackListAddDto";

import type { IUserBonusDto } from "api/interfaces/users/IUserBonusDto";
import type { IUserWithTelegramNameDto } from "api/interfaces/users/IUserWithTelegramNameDto";

import type { IUserBonusWithProblemDto } from "api/interfaces/users/IUserBonusWithProblemDto";

import type { IEmployeeHistoryReasonDismissFormValues } from "pages/User/Profile/ManagerEditorMenu/helper";

import { CRUDDataProvider, dataProvider } from "../dataProvider";

const baseUrl = "api/users";

export const currentUserUrl = "api/userCurrent";
const employeeUrl = "api/usersEmployee";
const UsersDataProvider = {
    manage: {
        url: "api/usersManage",
        addEmployee: (user: IUserSaveDto): Promise<IWebApiResult<IUserShortDto>> => {
            return dataProvider(`${UsersDataProvider.manage.url}/employee`, "post", user);
        },
        save: (
            user: IUserSaveDto,
            saveEmployee: boolean
        ): Promise<IWebApiResult<IUserShortDto>> => {
            return dataProvider(`${UsersDataProvider.manage.url}/${saveEmployee}`, "put", user);
        },
        changeRole: (
            userId: number,
            userRoleId: UserRoleEnum,
            gradeTreeIds: number[]
        ): Promise<IWebApiResult<boolean>> => {
            return dataProvider(`${UsersDataProvider.manage.url}/role`, "put", {
                userId,
                userRoleId,
                gradeTreeIds,
            });
        },
        updateUserPhone: (userId: number, phone: string): Promise<IWebApiResult<boolean>> => {
            return dataProvider(`${UsersDataProvider.manage.url}/phone`, "put", { userId, phone });
        },
        updateUserStatus: (
            userId: number,
            reason: IEmployeeHistoryReasonDismissFormValues | null,
            status: UserStatusEnum
        ): Promise<IWebApiResult<boolean>> => {
            let data = {
                userId,
                status,
            };
            if (reason) {
                data = { ...data, ...reason };
            }
            return dataProvider(`${UsersDataProvider.manage.url}/status`, "put", data);
        },
        updateEmployeeUserStatus: (
            userId: number,
            reason: IEmployeeHistoryReasonDismissFormValues,
            status: UserStatusEnum
        ): Promise<IWebApiResult<boolean>> => {
            return dataProvider(`${UsersDataProvider.manage.url}/dismissEmployee`, "put", {
                userId,
                status,
                ...reason,
            });
        },
        changePassword: (userId: number, password: string): Promise<IWebApiResult<boolean>> => {
            return dataProvider(`${UsersDataProvider.manage.url}/password`, "put", {
                userId,
                password,
            });
        },
        employeeUserRestore: (data: IUserRestoreDto): Promise<IWebApiResult<boolean>> => {
            return dataProvider(`${UsersDataProvider.manage.url}/restoreEmployee`, "put", data);
        },
        changeEmployeeUserTradePoint: (
            userId: number,
            tradePointId: number,
            employeeId: number,
            isOfficial: boolean,
            rate: number,
            officialRate: number
        ): Promise<IWebApiResult<boolean>> => {
            return dataProvider(`${UsersDataProvider.manage.url}/employeeUserMove`, "put", {
                userId,
                tradePointId,
                employeeId,
                isOfficial,
                rate,
                officialRate,
            });
        },
        updateUserSiteConfig: (
            userId: number,
            data: { deviceLimit?: number | null }
        ): Promise<IWebApiResult<boolean>> => {
            return dataProvider(`${UsersDataProvider.manage.url}/siteConfig`, "put", {
                userId,
                ...data,
            });
        },
    },
    current: {
        savePassword: (password: string): Promise<IWebApiResult<boolean>> => {
            return dataProvider(`${currentUserUrl}/password`, "put", password);
        },
        saveSiteConfig: (config: IUserSiteConfigDto): Promise<IWebApiResult<boolean>> => {
            return dataProvider(`${currentUserUrl}/siteConfig`, "put", config);
        },
        saveAvatar: (imageId: number): Promise<IWebApiResult<boolean>> => {
            return dataProvider(`${currentUserUrl}/avatar`, "put", imageId);
        },
        confirmPrivacyConsent: (): Promise<IWebApiResult<boolean>> => {
            return dataProvider(`${currentUserUrl}/privacyConsent`, "put", true);
        },
        getInitialUserData: (): Promise<IWebApiResult<IUserInitialDto>> => {
            return dataProvider(`${currentUserUrl}`, "get");
        },
        saveEmail: (email: string): Promise<IWebApiResult<boolean>> => {
            return dataProvider(`${currentUserUrl}/email`, "put", email);
        },
    },
    employee: {
        getHistory: (userId: number): Promise<IWebApiResult<IEmployeeHistoryDto[]>> => {
            return dataProvider(`${employeeUrl}/history/${userId}`, "get");
        },
    },
    getAll: (active: boolean = false): Promise<IWebApiResult<IUserDto[]>> => {
        return dataProvider(`${baseUrl}/all/${active}`, "get");
    },
    getAllEmployees: (): Promise<IWebApiResult<IUserDto[]>> => {
        return dataProvider(`${baseUrl}/employees`, "get");
    },
    getListWithTelegramUsername: (
        active: boolean = false
    ): Promise<IWebApiResult<IUserWithTelegramNameDto[]>> => {
        return dataProvider(`${baseUrl}/list/${active}`, "get");
    },
    getAllAvailable: (): Promise<IWebApiResult<IUserDto[]>> => {
        return dataProvider(`${baseUrl}/available`, "get");
    },
    getAllAvailableByTradePoint: (tpId: number): Promise<IWebApiResult<IUserDto[]>> => {
        return dataProvider(`${baseUrl}/available/${tpId}`, "get");
    },
    checkUserExist: (
        phone: string,
        lastName: string,
        firstName: string
    ): Promise<IWebApiResult<IUserExistCheckerDto>> => {
        return dataProvider(`${baseUrl}/checkExist`, "get", { phone, lastName, firstName });
    },

    getUserData: (id: number): Promise<IWebApiResult<IUserFullDto>> => {
        return dataProvider(`${baseUrl}/${id}`, "get");
    },

    getUserDataByGuid: (id: string): Promise<IWebApiResult<IUserFullDto>> => {
        return dataProvider(`${baseUrl}/byGuid/${id}`, "get");
    },
    blackList: {
        getRecords: (): Promise<IWebApiResult<IUserBlackListDto[]>> => {
            return dataProvider(`api/users-black-list`);
        },
        add: (data: IUserBlackListAddDto): Promise<IWebApiResult<boolean>> => {
            return dataProvider(`api/users-black-list`, "post", data);
        },
        remove: (userId: number): Promise<IWebApiResult<boolean>> => {
            return dataProvider(`api/users-black-list/${userId}`, "delete");
        },
    },
    bonuses: {
        ...new CRUDDataProvider<IUserBonusDto>("api/users-bonus", false).getData(),
        getProblemUsers: (): Promise<IWebApiResult<IUserBonusWithProblemDto[]>> => {
            return dataProvider(`api/users-bonus/problemUsers`, "get");
        },
        removeUser: (userId: number): Promise<IWebApiResult<boolean>> => {
            return dataProvider(`api/users-bonus/removeUser/${userId}`, "delete");
        },
    },
};
export default UsersDataProvider;
